/**
 * Phase 2 Demo Data
 * Sample op note text and pre-configured clarifications for demo mode
 */

// Sample operative note text for demo mode
export const DEMO_OP_NOTE_TEXT = `
OPERATIVE REPORT

PATIENT: [Demo Patient]
DATE OF SURGERY: [Current Date]
SURGEON: Dr. Larkin

PREOPERATIVE DIAGNOSIS:
Right knee osteoarthritis, grade IV

POSTOPERATIVE DIAGNOSIS:
Same

PROCEDURE PERFORMED:
1. Right total knee arthroplasty
2. Patella resurfacing
3. Femoral and tibial component placement

ANESTHESIA: General

INDICATIONS:
The patient is a 65-year-old with severe right knee osteoarthritis with bone-on-bone 
changes on weight-bearing radiographs. Conservative treatment including physical therapy, 
activity modification, and intra-articular injections failed to provide adequate relief.

PROCEDURE DETAILS:
The patient was brought to the operating room and placed in the supine position. 
After induction of general anesthesia, the right lower extremity was prepped and 
draped in the usual sterile fashion. A tourniquet was placed on the right thigh.

INCISION AND EXPOSURE:
A standard midline incision was made. The medial parapatellar approach was utilized. 
The patella was everted and the knee was flexed.

TIBIAL PREPARATION:
The tibial cut was made using the extramedullary guide. The tibial plateau was 
resected perpendicular to the mechanical axis. The posterior slope was set at 3 degrees.

FEMORAL PREPARATION:
The femoral canal was entered and the intramedullary guide was placed. Distal femoral 
resection was performed at 5 degrees of valgus. Anterior and posterior femoral cuts 
were made using the sizing guide. Trial components were placed.

PATELLA RESURFACING:
The patella was prepared using the dome technique. The resection was performed 
maintaining adequate bone stock. A 32mm all-polyethylene patella component was selected.

COMPONENT IMPLANTATION:
After pulsatile lavage, cement was mixed and applied. The tibial baseplate was impacted. 
The femoral component was seated. The polyethylene insert was clicked into place. 
The patella component was cemented.

CLOSURE:
The tourniquet was released after 62 minutes. Hemostasis was achieved. The wound was 
irrigated copiously. The arthrotomy was closed with interrupted #1 Vicryl sutures. 
The subcutaneous tissue was closed with 2-0 Vicryl. The skin was closed with staples.

ESTIMATED BLOOD LOSS: 150 mL
TOURNIQUET TIME: 62 minutes
IMPLANTS USED:
- Femoral Component: Size 4, Cemented
- Tibial Baseplate: Size 3, Cemented  
- Polyethylene Insert: 10mm
- Patella: 32mm all-polyethylene

DISPOSITION:
The patient tolerated the procedure well and was transferred to the recovery room 
in stable condition.

Electronically signed by: Dr. Michael Larkin, MD
`;

// Demo reconciliation entries (pre-confirmed)
export const DEMO_RECONCILIATION_ENTRIES = [
  {
    cptCode: '27447',
    cptDescription: 'Total knee arthroplasty',
    status: 'confirmed_performed' as const,
    surgeonConfirmed: true,
    isBillable: true,
    billableReason: 'Demo mode auto-confirmation',
    isDemoEntry: true,
  },
  {
    cptCode: '27438',
    cptDescription: 'Patella resurfacing',
    status: 'confirmed_performed' as const,
    surgeonConfirmed: true,
    isBillable: true,
    billableReason: 'Demo mode auto-confirmation',
    isDemoEntry: true,
  },
];

// Demo clarification questions (all answered "Yes")
export const DEMO_CLARIFICATION_QUESTIONS = [
  {
    cptCode: '27447',
    requiredElement: 'Tibial component placement',
    questionText: 'Was the tibial component placed during this procedure?',
    matchConfidence: 'found' as const,
    matchedText: 'The tibial baseplate was impacted',
    surgeonResponse: 'yes_performed' as const,
    isDemoResponse: true,
  },
  {
    cptCode: '27447',
    requiredElement: 'Femoral component placement',
    questionText: 'Was the femoral component placed during this procedure?',
    matchConfidence: 'found' as const,
    matchedText: 'The femoral component was seated',
    surgeonResponse: 'yes_performed' as const,
    isDemoResponse: true,
  },
  {
    cptCode: '27438',
    requiredElement: 'Patella resurfacing technique',
    questionText: 'Was patella resurfacing performed using an approved technique?',
    matchConfidence: 'found' as const,
    matchedText: 'The patella was prepared using the dome technique',
    surgeonResponse: 'yes_performed' as const,
    isDemoResponse: true,
  },
];

// Demo Phase 3 insights (read-only, illustrative)
export const DEMO_PHASE3_INSIGHTS = {
  revenueFlags: [
    {
      id: 'demo-flag-1',
      type: 'authorized_not_confirmed' as const,
      cptCode: '27446',
      severity: 'low' as const,
      message: 'Historically, CPT 27446 has been authorized but not confirmed as performed in 12% of similar cases over the last 6 months. This may indicate a documentation workflow pattern.',
      historicalContext: {
        occurrenceCount: 3,
        observationPeriod: 'last 6 months',
        occurrencePct: 12,
        minimumCaseThreshold: 5,
      },
      isDismissed: false,
    },
    {
      id: 'demo-flag-2',
      type: 'documentation_missing' as const,
      cptCode: '27447',
      severity: 'medium' as const,
      message: 'In historical cases, CPT 27447 frequently had incomplete documentation elements (6% of cases over the last 12 months). This observation may be relevant to current documentation.',
      historicalContext: {
        occurrenceCount: 2,
        observationPeriod: 'last 12 months',
        occurrencePct: 6,
        minimumCaseThreshold: 5,
      },
      isDismissed: false,
    },
  ],
  patternMetrics: {
    id: 'demo-metrics-1',
    totalCasesAnalyzed: 47,
    reconciliationRate: 96.2,
    documentationCompliance: 94.8,
    denialAvoidance: 98.1,
    periodStart: '2025-08-01',
    periodEnd: '2026-02-01',
    minimumCaseThreshold: 5,
    totalAuthorizedCpts: 142,
    totalConfirmedPerformed: 136,
    totalExportedToEhr: 134,
    totalDeniedPostSubmission: 3,
    cptsFrequentlyAuthorizedNotConfirmed: [
      { cpt: '27446', description: 'Medial/lateral knee arthroplasty', count: 4 },
    ],
    cptsWithHighDenialRate: [],
    modifierMismatchPatterns: [],
    isDemoData: true,
  },
  patternSummary: {
    reconciliationRate: 96.2,
    documentationCompliance: 94.8,
    denialAvoidance: 98.1,
    trendVsPriorPeriod: {
      reconciliationRateChange: 1.5,
      documentationComplianceChange: 0.8,
      denialAvoidanceChange: 0.3,
    },
    isDemoData: true,
  },
  patternInsights: [
    {
      id: 'demo-insight-1',
      category: 'documentation_pattern',
      severity: 'low' as const,
      observation: 'Operative notes for total knee arthroplasty procedures historically include all required elements in 94.8% of cases.',
      affectedCpts: ['27447'],
      periodStart: '2025-08-01',
      periodEnd: '2026-02-01',
      actualCaseCount: 47,
      isDemoData: true,
    },
  ],
};
